import type { Environment, KeyIndexEntry } from '@fleequid-secrets/shared';
import { useQuery } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useMemo, useState } from 'react';
import { api } from '@/api/client';
import type { Command } from '@/lib/shell';
import { useOverlay } from '@/lib/overlay';
import { Icon, type IconName } from './Icon';

interface Item {
    group: string;
    icon: IconName;
    label: string;
    mono?: boolean;
    sub?: ReactNode;
    key?: string;
    run: () => void;
    // ⌘↵ on a variable copies its value without opening it.
    copy?: () => void;
}

const LIMIT = 40;

// ⌘K: a key from any environment (by name, never by value), a section, an environment, or a command. ↵ opens, ⌘↵
// copies a variable's value straight to the clipboard.
export function Palette(props: {
    environments: readonly Environment[];
    commands: Command[];
    onOpenKey: (entry: KeyIndexEntry) => void;
    onCopyKey: (entry: KeyIndexEntry) => void;
    onOpenSection: (environment: Environment, sectionSlug: string) => void;
    onClose: () => void;
}): ReactNode {
    useOverlay();
    const [input, setInput] = useState('');
    const [index, setIndex] = useState(0);
    const keys = useQuery({ queryKey: ['keys'], queryFn: api.keys, staleTime: 15_000 });
    const where = useMemo(() => {
        const map = new Map<string, { environment: Environment; section: Environment['sections'][number] }>();
        for (const environment of props.environments) {
            for (const section of environment.sections) {
                map.set(section.id, { environment, section });
            }
        }

        return map;
    }, [props.environments]);

    const words = input.trim().toLowerCase();
    const items = useMemo((): Item[] => {
        const place = (environment: Environment, section?: string): ReactNode => (
            <>
                <i className="dot" style={{ '--c': environment.color } as CSSProperties} />
                {environment.name}
                {section ? ` · ${section}` : ''}
            </>
        );
        const found: Item[] = [];
        if (words) {
            const matches = (keys.data?.keys ?? [])
                .filter((entry) => entry.key.toLowerCase().includes(words))
                .sort(
                    (a, b) =>
                        Number(!a.key.toLowerCase().startsWith(words)) - Number(!b.key.toLowerCase().startsWith(words)) || a.key.localeCompare(b.key),
                );
            for (const entry of matches.slice(0, LIMIT)) {
                const at = where.get(entry.sectionId);
                if (at) {
                    found.push({
                        group: 'Variables',
                        icon: 'key',
                        label: entry.key,
                        mono: true,
                        sub: place(at.environment, at.section.name),
                        run: () => props.onOpenKey(entry),
                        copy: () => props.onCopyKey(entry),
                    });
                }
            }
            for (const environment of props.environments) {
                for (const section of environment.sections) {
                    if (section.name.toLowerCase().includes(words)) {
                        found.push({
                            group: 'Sections',
                            icon: 'file',
                            label: section.name,
                            sub: place(environment),
                            run: () => props.onOpenSection(environment, section.slug),
                        });
                    }
                }
            }
        }
        const commands = props.commands
            .filter((command) => !words || command.label.toLowerCase().includes(words))
            .map((command) => ({ group: words ? 'Commands' : 'Go and do', ...command }));

        return [...found, ...commands];
    }, [words, keys.data, where, props]);
    const active = Math.min(index, Math.max(0, items.length - 1));

    const pick = (item: Item | undefined, copy: boolean): void => {
        if (!item) {
            return;
        }
        if (copy && item.copy) {
            item.copy();
        } else {
            item.run();
        }
        props.onClose();
    };

    return (
        <div className="scrim" onMouseDown={(event) => event.target === event.currentTarget && props.onClose()}>
            <div className="palette float">
                <div className="pin">
                    <Icon name="search" size={19} />
                    <input
                        autoFocus
                        value={input}
                        spellCheck={false}
                        placeholder="Find a variable by its key, a section, or a command"
                        onChange={(event) => {
                            setInput(event.target.value);
                            setIndex(0);
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'ArrowDown') {
                                event.preventDefault();
                                setIndex((active + 1) % Math.max(1, items.length));
                            } else if (event.key === 'ArrowUp') {
                                event.preventDefault();
                                setIndex((active - 1 + items.length) % Math.max(1, items.length));
                            } else if (event.key === 'Enter') {
                                event.preventDefault();
                                pick(items[active], event.metaKey || event.ctrlKey);
                            } else if (event.key === 'Escape') {
                                props.onClose();
                            }
                        }}
                    />
                </div>
                <div className="pl">
                    {words && items.length === 0 && <div className="pg">Nothing called “{input.trim()}”</div>}
                    {items.map((item, i) => (
                        <div key={`${item.group}-${item.label}-${i}`}>
                            {(i === 0 || items[i - 1]?.group !== item.group) && <div className="pg">{item.group}</div>}
                            <button
                                className={`pi ${i === active ? 'on' : ''}`}
                                onMouseEnter={() => setIndex(i)}
                                onClick={(event) => pick(item, event.metaKey || event.ctrlKey)}
                            >
                                <span className="ico">
                                    <Icon name={item.icon} size={15} />
                                </span>
                                <span>
                                    <span className={`label ${item.mono ? 'mono' : ''}`}>{item.label}</span>
                                    {item.sub && <div className="sub">{item.sub}</div>}
                                </span>
                                {item.key && <span className="kbd k">{item.key}</span>}
                                {item.copy && i === active && <span className="kbd k">⌘↵ copy</span>}
                            </button>
                        </div>
                    ))}
                </div>
                <div className="pfoot">
                    <span>
                        <span className="kbd">↑↓</span> move
                    </span>
                    <span>
                        <span className="kbd">↵</span> open
                    </span>
                    <span>
                        <span className="kbd">⌘↵</span> copy the value
                    </span>
                    <span>
                        <span className="kbd">Esc</span> close
                    </span>
                </div>
            </div>
        </div>
    );
}
